import React, { useEffect } from 'react';
import { v4 } from 'uuid';
import { FieldArray } from 'formik';
import Text from '@service-victoria/component-library/components/Text';
import TaskButton from '@service-victoria/component-library/components/TaskButton';
import Input from '@service-victoria/component-library/components/Input';
import RadioButton, {
  ItemType as RadiobuttonItem
} from '@service-victoria/component-library/components/RadioButton';
import { StepComponent } from '@service-victoria/component-library/components/Wizard/props';

import * as Interfaces from 'interfaces';
import * as Styled from '../styled';
import useTravelReasons from 'hooks/useTravelReasons';
import PhoneInput from 'Components/PhoneInput';

export const whoIsTravelingSelectableOptions: RadiobuttonItem[] = [
  {
    label: <span aria-label='I am the only person travelling on this permit'>Just me</span>,
    value: 'Me',
    inputProps: {
      'data-testid': 'JustMe-RadioButton'
    }
  },
  {
    label: (
      <span aria-label='I am travelling with myself and my dependants, or anyone under the age of 12'>
        Me and my dependants or anyone under 12 years and 2 months of age
      </span>
    ),
    value: 'Me and my dependants or anyone under 12',
    inputProps: {
      'data-testid': 'Dependants-RadioButton'
    }
  }
];

export type ComponentProps = StepComponent<Pick<Interfaces.Common.FormValues, 'identity'>>;

const getNewTravellerItem: () => Interfaces.Common.OtherTraveller = () => ({
  id: v4(),
  name: ''
});

/** Disable Phone masking
 * Refer: https://servicevic.atlassian.net/browse/CBP-73
function getPhoneMask(value) {
  return {
    mask:
      value !== undefined
        ? value.charAt(0) === '+'
          ? value.charAt(3) === '4' || value.charAt(4) === '4'
            ? 'abc dxx xxx xxx'
            : 'abcd xxxx xxxx'
          : value.charAt(0) === '0'
          ? value.charAt(1) === '4'
            ? 'adxx xxx xxx'
            : 'ad xxxx xxxx'
          : value.charAt(0) === '1' && value.charAt(1) === '8'
          ? 'aeff xxx xxx'
          : value.charAt(0) === '1' &&
            value.charAt(1) === '3' &&
            value.charAt(2) === '0' &&
            (value.charAt(3) === '0' || value.charAt(4) === '0')
          ? 'aeff xxx xxx'
          : 'aex xxx'
        : '',
    formatChars: {
      x: '[0-9]',
      a: '[+01]',
      b: '[6]',
      c: '[1]',
      d: '[23478]',
      e: '[38]',
      f: '[0]'
    }
  };
}
**/

const Identity: React.FC<ComponentProps> = ({ context }) => {
  const {
    form: { values, setFieldValue, handleBlur, handleChange }
  } = context;

  const {
    identity: { givenName, familyName, whoIsTravelling, othersTravel },
    assess
  } = values as Interfaces.Common.FormValues;

  useTravelReasons();

  /** Disable Phone masking
   * Refer: https://servicevic.atlassian.net/browse/CBP-73
   * Todo: Investigate issues with Input masking.
   const maskingOptions = getPhoneMask(values.identity.phone);
  * */

  const lastTravellerInput = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (othersTravel.length) {
      lastTravellerInput.current?.focus();
    }
  }, [othersTravel]);

  useEffect(() => {
    if (whoIsTravelling && whoIsTravelling === 'Me' && othersTravel?.length > 0) {
      setFieldValue('identity.othersTravel', [], false);
    } else if (whoIsTravelling && whoIsTravelling !== 'Me' && othersTravel?.length === 0) {
      setFieldValue('identity.othersTravel', [getNewTravellerItem()], false);
    }
  }, [setFieldValue, whoIsTravelling, othersTravel]);

  // CBP-274 Update Supporting docs first/last name based on identity
  useEffect(() => {
    const { driversLicence } = assess.SupportingDocuments || {};
    if (driversLicence?.givenNames && driversLicence?.familyName) {
      setFieldValue('assess.SupportingDocuments.driversLicence.givenNames', givenName, false);
      setFieldValue('assess.SupportingDocuments.driversLicence.familyName', familyName, false);
    }
  }, [setFieldValue, givenName, familyName, assess.SupportingDocuments]);

  return (
    <Styled.Wrapper>
      <Text.Heading.H1 fontWeight={600}>Your details</Text.Heading.H1>
      <Styled.Spacer24 />
      <Text.Heading.H2 fontWeight={600}>
        Your permit must be valid at the time of travel.
      </Text.Heading.H2>
      <Text.Body>
        Conditions may change at any time. If conditions change you may need to reapply.
      </Text.Body>
      <Text.Body fontWeight={600}>
        AUD$5452 fines apply for providing incorrect information.
      </Text.Body>

      <Styled.Spacer32 />
      <Input
        name='identity.givenName'
        label='Given name'
        ariaLabel='Given name'
        maxLength={40}
        data-testid='GivenName-Input'
        errorLabelProps={{
          'data-testid': 'GivenName-InputError'
        }}
      />
      <Styled.Spacer24 />
      <Input
        name='identity.familyName'
        label='Family name'
        ariaLabel='Family name'
        maxLength={40}
        data-testid='FamilyName-Input'
        errorLabelProps={{
          'data-testid': 'FamilyName-InputError'
        }}
      />
      <Styled.Spacer24 />
      <PhoneInput label='Phone number' name='identity.phone' />
      <Styled.Spacer24 />
      <Input
        name='identity.email'
        label='Your email'
        type='email'
        maxLength={80}
        data-testid='Email-Input'
        errorLabelProps={{
          'data-testid': 'Email-InputError'
        }}
      />
      <Styled.Spacer24 />
      <Input
        name='identity.confirmEmail'
        label='Confirm your email'
        type='email'
        maxLength={80}
        data-testid='ConfirmEmail-Input'
        errorLabelProps={{
          'data-testid': 'ConfirmEmail-InputError'
        }}
      />
      <Styled.Spacer32 />
      <RadioButton
        name='identity.whoIsTravelling'
        groupLabel='Who is travelling?'
        items={whoIsTravelingSelectableOptions}
        errorLabelProps={{
          'data-testid': 'Travelling-RadioButtonError'
        }}
      />
      {whoIsTravelling === 'Me and my dependants or anyone under 12' && (
        <>
          <Styled.NotificationInfo
            style={{ marginTop: '12px' }}
            value='Any dependants who have not been vaccinated and are travelling with you must follow the same permit conditions as you.'
          />
          <Styled.Spacer24 />
          <FieldArray
            name='identity.othersTravel'
            render={arrayHelpers => {
              return (
                <Styled.Wrapper>
                  {othersTravel?.map((traveller, index) => {
                    return (
                      <Styled.ContentWrapper key={traveller.id}>
                        <Input
                          ariaLabel={`Name of dependant ${
                            index + 1
                          } (other than myself) travelling`}
                          name={`identity.othersTravel.${index}.name`}
                          label='Name'
                          maxLength={81}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={traveller.name}
                          data-testid={`Dependant${index}-Input`}
                          errorLabelProps={{
                            'data-testid': `Dependant${index}-InputError`
                          }}
                          {...(index === othersTravel.length - 1
                            ? { ref: lastTravellerInput }
                            : {})}
                        />
                        {othersTravel.length > 1 && (
                          <TaskButton
                            aria-label={`Remove dependant ${index + 1} from the permit application`}
                            iconType='delete'
                            data-testid={`RemoveDependant-${index}-Button`}
                            onClick={e => {
                              e.preventDefault();
                              arrayHelpers.remove(index);
                            }}>
                            Remove
                          </TaskButton>
                        )}
                      </Styled.ContentWrapper>
                    );
                  })}
                  <TaskButton
                    aria-label='Add another dependant to the permit application'
                    type='button'
                    iconType='add'
                    styleType='largeWithBorder'
                    data-testid='AddDependant-Button'
                    onClick={() => {
                      arrayHelpers.push(getNewTravellerItem());
                    }}>
                    Add name
                  </TaskButton>
                </Styled.Wrapper>
              );
            }}
          />
        </>
      )}
    </Styled.Wrapper>
  );
};

Identity.displayName = 'Identity';

export default Identity;
